/* prettier-ignore-file */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  Dimensions,
} from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthHeader from '../components/AuthHeader';
import { useSettings, useAuthStore } from '../stores';
import PinAuthLayer from '../components/PinAuthLayer';
import { isDeviceTrusted } from '../utils/deviceTrust';

const ViewWaivers = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { waivers = [], bookingId } = location.state || {};
  
  // Track which sections are expanded for each waiver
  const [expandedSections, setExpandedSections] = useState({});
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  
  // Get settings and auth from stores
  const settings = useSettings((state) => state.settings);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [isVerified, setIsVerified] = useState(false);
  const storeName = settings?.storeName?.name || "";
  const storeLogo = settings?.storeLogo?.url || "";
  const shopInfo = {
    address: settings?.startingAddress?.address || "",
    phone: settings?.storePhone?.phone || "",
    email: settings?.storeEmail?.email || "",
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check PIN authentication for unauthenticated users
  useEffect(() => {
    if (isAuthenticated) {
      setIsVerified(true);
      return;
    }

    if (!bookingId) {
      return;
    }

    // Check if device is trusted
    const trustInfo = isDeviceTrusted(bookingId);
    if (trustInfo) {
      console.log("✅ Device is trusted for ViewWaivers");
      setIsVerified(true);
    } else {
      console.log("🔒 Device not trusted, showing PIN auth");
      setIsVerified(false);
    }
  }, [bookingId, isAuthenticated]);

  const toggleSection = (waiverIndex, sectionKey) => {
    const key = `${waiverIndex}-${sectionKey}`;
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isSectionExpanded = (waiverIndex, sectionKey) => {
    const key = `${waiverIndex}-${sectionKey}`;
    return expandedSections[key] || false;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Handle Firestore timestamp object
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }
    
    // Handle ISO string
    if (typeof timestamp === 'string') {
      return new Date(timestamp).toLocaleString();
    }
    
    return 'N/A';
  };

  const renderInitialSection = (waiverIndex, title, content, initialData) => {
    const isExpanded = isSectionExpanded(waiverIndex, title);
    
    return (
      <View key={title} style={styles.initialSection}>
        <TouchableOpacity
          style={styles.sectionHeader}
          onPress={() => toggleSection(waiverIndex, title)}
        >
          <Text style={styles.sectionTitle}>{title}</Text>
          <Text style={styles.toggleIcon}>{isExpanded ? '▼' : '▶'}</Text>
        </TouchableOpacity>
        
        {isExpanded && (
          <View style={styles.sectionContent}>
            <Text style={styles.sectionText}>{content}</Text>
            <View style={styles.initialContainer}>
              <Text style={styles.initialLabel}>Customer Initial:</Text>
              {initialData ? (
                <Image
                  source={{ uri: initialData }}
                  style={styles.initialImage}
                  resizeMode="contain"
                />
              ) : (
                <Text style={styles.noDataText}>No initial provided</Text>
              )}
            </View>
          </View>
        )}
      </View>
    );
  };

  const renderWaiver = (waiver, index) => {
    const {
      customerName,
      typedName,
      homeAddress,
      signedAt,
      fullSignature,
      initial1,
      initial2,
      initial3,
      initial4,
      initialCancellation,
      waiverType,
      pdfUrl,
      isOriginalBooker,
    } = waiver;

    // Define section content (these should match the waiver template sections)
    const sections = [
      {
        title: '1. EQUIPMENT RESPONSIBILITY AND CARE',
        content: 'I, the undersigned renter, accept FULL RESPONSIBILITY for ALL rented equipment, whether I personally use it or not. I am responsible for the care, condition, security, and proper use of ALL equipment rented under this agreement during the ENTIRE rental period, regardless of who uses the equipment.',
        initial: initial1
      },
      {
        title: '2. RELEASE OF LIABILITY',
        content: 'I hereby RELEASE, WAIVE, and DISCHARGE the rental company, its owners, employees, agents, and representatives from ANY AND ALL LIABILITY, claims, demands, or causes of action arising from personal injury, property damage, or any other loss sustained while using the rented equipment.',
        initial: initial2
      },
      {
        title: '3. RETURN AND CHARGES',
        content: 'I understand that I must return ALL equipment in the same condition as received, normal wear and tear excepted. I agree to pay for any damage, loss, or theft of equipment. Late returns will incur additional charges.',
        initial: initial3
      },
      {
        title: '4. INDEMNIFICATION',
        content: 'I agree to INDEMNIFY, DEFEND, and HOLD HARMLESS the rental company from and against any and all claims, liabilities, damages, losses, or expenses (including attorney\'s fees) arising out of or resulting from my use of the rented equipment.',
        initial: initial4
      },
    ];

    // Add cancellation policy section if initial exists
    if (initialCancellation) {
      sections.push({
        title: '5. CANCELLATION POLICY',
        content: 'I have read and agree to the cancellation policy. I understand the refund percentages and time requirements for cancellations.',
        initial: initialCancellation
      });
    }

    return (
      <View key={index} style={styles.waiverCard}>
        {/* Header */}
        <View style={styles.waiverHeader}>
          <Text style={styles.waiverTitle}>
            {isOriginalBooker ? '📝 Original Booking Customer' : '📝 Additional Signer'}
          </Text>
          <Text style={styles.waiverType}>
            {waiverType === 'bicycle' ? '🚴 Bicycle Rental' : '🛶 Equipment Rental'}
          </Text>
        </View>

        {/* Basic Information */}
        <View style={styles.infoSection}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Customer Name:</Text>
            <Text style={styles.infoValue}>{customerName || 'N/A'}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Typed Legal Name:</Text>
            <Text style={styles.infoValue}>{typedName || 'N/A'}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Home Address:</Text>
            <Text style={styles.infoValue}>{homeAddress || 'N/A'}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Signed Date:</Text>
            <Text style={styles.infoValue}>{formatDate(signedAt)}</Text>
          </View>
        </View>

        {/* Initial Sections (Collapsible) */}

        {/* Full Signature */}
        <View style={styles.signatureSection}>
          <Text style={styles.signatureLabel}>Full Legal Signature:</Text>
          {fullSignature ? (
            <View style={styles.signatureContainer}>
              <Image
                source={{ uri: fullSignature }}
                style={styles.signatureImage}
                resizeMode="contain"
              />
            </View>
          ) : (
            <Text style={styles.noDataText}>No signature provided</Text>
          )}
        </View>

        {/* PDF Link */}
        {pdfUrl && (
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={styles.pdfButton}
            onPress={() => window.open(pdfUrl, '_blank')}
          >
            <Text style={styles.pdfButtonText}>📄 View PDF Document</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  if (!waivers || waivers.length === 0) {
    return (
      <View style={styles.container}>
        <AuthHeader
          title="View Waivers"
          subtitle={storeName ? `by ${storeName}` : ""}
          isDesktop={isDesktop}
          isAuthenticated={isAuthenticated}
          shopInfo={shopInfo}
          storeLogo={storeLogo}
        />
        <View style={isDesktop ? styles.desktopWrapper : styles.content}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No waivers found for this booking.</Text>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.backButton}
              onPress={() => navigate(`/view-booking/${bookingId}`)}
            >
              <Text style={styles.backButtonText}>← Back to Booking</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  }

  // Show PIN auth layer for unauthenticated users
  if (!isAuthenticated && !isVerified && bookingId) {
    return (
      <View style={{ flex: 1, backgroundColor: "#F6F8F7" }}>
        <AuthHeader
          title="View Waivers"
          subtitle=""
          isDesktop={isDesktop}
          isAuthenticated={isAuthenticated}
          shopInfo={shopInfo}
          storeLogo={storeLogo}
        />
        <PinAuthLayer
          onAuthenticated={() => {
            console.log("✅ PIN verified for ViewWaivers:", bookingId);
            setIsVerified(true);
          }}
          bookingId={bookingId}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <AuthHeader
        title="View Waivers"
        subtitle={storeName ? `by ${storeName}` : ""}
        isDesktop={isDesktop}
        isAuthenticated={isAuthenticated}
        shopInfo={shopInfo}
        storeLogo={storeLogo}
      />
      <View style={isDesktop ? styles.desktopWrapper : null}>
        <ScrollView style={styles.scrollView}>
          <View style={styles.pageHeader}>
            <Text style={styles.pageTitle}>Signed Waivers</Text>
            <Text style={styles.pageSubtitle}>
              {waivers.length} {waivers.length === 1 ? 'Waiver' : 'Waivers'} on File
            </Text>
          </View>

          {waivers.map((waiver, index) => renderWaiver(waiver, index))}

          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={styles.backButton}
            onPress={() => navigate(`/view-booking/${bookingId}`)}
          >
            <Text style={styles.backButtonText}>← Back to Booking</Text>
          </TouchableOpacity>

          <View style={{ height: 40 }} />
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F6F8F7',
  },
  desktopWrapper: {
    width: '100%',
    maxWidth: 800,
    flex: 1,
    alignSelf: 'center',
  },
  content: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  pageHeader: {
    backgroundColor: '#FFFFFF',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#DDE3E2',
    marginBottom: 16,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2E3A38',
    marginBottom: 4,
  },
  pageSubtitle: {
    fontSize: 14,
    color: '#5C6B68',
  },
  waiverCard: {
    backgroundColor: '#FFFFFF',
    margin: 16,
    marginBottom: 8,
    borderRadius: 8,
    padding: 16,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  waiverHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 2,
    borderBottomColor: '#0E7C6B',
  },
  waiverTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0E7C6B',
  },
  waiverType: {
    fontSize: 14,
    color: '#5C6B68',
    fontWeight: '600',
  },
  infoSection: {
    marginBottom: 20,
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  infoLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#5C6B68',
    minWidth: 140,
  },
  infoValue: {
    fontSize: 14,
    color: '#2E3A38',
    flex: 1,
  },
  sectionsContainer: {
    marginTop: 12,
    marginBottom: 20,
  },
  sectionsHeader: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2E3A38',
    marginBottom: 12,
  },
  initialSection: {
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#DDE3E2',
    borderRadius: 6,
    overflow: 'hidden',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#F4F6F6',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2E3A38',
    flex: 1,
  },
  toggleIcon: {
    fontSize: 14,
    color: '#0E7C6B',
    marginLeft: 8,
  },
  sectionContent: {
    padding: 12,
    backgroundColor: '#FFFFFF',
  },
  sectionText: {
    fontSize: 13,
    lineHeight: 20,
    color: '#5C6B68',
    marginBottom: 12,
  },
  initialContainer: {
    marginTop: 8,
  },
  initialLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#5C6B68',
    marginBottom: 8,
  },
  initialImage: {
    width: 150,
    height: 50,
    borderWidth: 1,
    borderColor: '#DDE3E2',
    borderRadius: 4,
    backgroundColor: '#F4F6F6',
  },
  signatureSection: {
    marginTop: 12,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#DDE3E2',
  },
  signatureLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2E3A38',
    marginBottom: 12,
  },
  signatureContainer: {
    borderWidth: 1,
    borderColor: '#DDE3E2',
    borderRadius: 6,
    padding: 8,
    backgroundColor: '#F4F6F6',
    alignItems: 'center',
  },
  signatureImage: { width: "100%", height: 220, resizeMode: "contain" },
  noDataText: {
    fontSize: 13,
    color: '#8A9794',
    fontStyle: 'italic',
  },
  pdfButton: {
    marginTop: 16,
    backgroundColor: '#0E7C6B',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: 'center',
  },
  pdfButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  backButton: {
    backgroundColor: '#0E7C6B',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 6,
    alignItems: 'center',
    marginHorizontal: 16,
    marginTop: 24,
  },
  backButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyText: {
    fontSize: 16,
    color: '#5C6B68',
    marginBottom: 24,
    textAlign: 'center',
  },
});

export default ViewWaivers;

